import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, FlaskConical, LogOut, Flag, LifeBuoy, BadgeCheck,
  Coins as CoinsIcon, Mail, Heart, ScrollText, UserCog, Shield,
  Settings as SettingsIcon, MoreHorizontal, Menu as MenuIcon, ChevronDown,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Permission-aware nav (UX only — backend RBAC is the security boundary)
const PRIMARY_ITEMS = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard, perm: "analytics.read" },
  { path: "/admin/users", label: "Users", icon: Users, perm: "members.read" },
  { path: "/admin/reports", label: "Reports", icon: Flag, perm: "moderation.read" },
  { path: "/admin/support", label: "Support", icon: LifeBuoy, perm: "support.read" },
  { path: "/admin/transactions", label: "Finance", icon: CoinsIcon, perm: "finance.read" },
  { path: "/admin/tester-invites", label: "Comms", icon: Mail, perm: "comms.send" },
];

const SECONDARY_ITEMS = [
  { path: "/admin/verifications", label: "Verifications", icon: BadgeCheck, perm: "verification.update" },
  { path: "/admin/matchmaker", label: "Matchmaker", icon: Heart, perm: "matchmaking.tools" },
  { path: "/admin/beta", label: "Beta Hub", icon: FlaskConical, perm: "members.read" },
  { path: "/admin/audit", label: "Audit Log", icon: ScrollText, perm: "audit.read" },
  { path: "/admin/team", label: "Team", icon: UserCog, perm: "team.manage" },
  { path: "/admin/security", label: "Security", icon: Shield, perm: "security.admin" },
  { path: "/admin/settings", label: "Settings", icon: SettingsIcon, perm: null },
];

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  let adminUser = null;
  try { adminUser = JSON.parse(localStorage.getItem("recoverymatch_admin_user")); } catch {}
  const hasPerm = (perm) =>
    !perm || adminUser?.is_super_admin || (adminUser?.permissions || []).includes(perm);

  const primary = PRIMARY_ITEMS.filter((i) => hasPerm(i.perm));
  const secondary = SECONDARY_ITEMS.filter((i) => hasPerm(i.perm));
  const allItems = [...primary, ...secondary];

  const isActive = (path) =>
    path === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(path);
  const moreActive = secondary.some((i) => isActive(i.path));

  const handleLogout = () => {
    localStorage.removeItem("recoverymatch_admin_token");
    localStorage.removeItem("recoverymatch_admin_user");
    navigate("/admin/login");
  };

  const navBtnClass = (active) =>
    `relative flex h-16 shrink-0 items-center gap-1.5 whitespace-nowrap px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FAB710] focus-visible:ring-inset ${
      active ? "text-white" : "text-white/70 hover:text-white hover:bg-white/10"
    }`;

  const ActiveBar = () => (
    <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#FAB710]" aria-hidden="true" />
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Top Navigation */}
      <nav aria-label="Admin navigation" className="bg-[#1C3A7A] text-white shadow-md sticky top-0 z-50">
        <div className="mx-auto flex h-16 max-w-7xl items-center px-4">
          {/* Logo/Brand — anchored left */}
          <button
            data-testid="admin-nav-logo"
            onClick={() => navigate("/admin")}
            className="flex shrink-0 items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FAB710] rounded-md"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FAB710]">
              <span className="text-sm font-bold text-[#1C3A7A]">RM</span>
            </span>
            <span className="hidden text-lg font-semibold sm:block">Admin</span>
          </button>

          {/* Desktop nav (lg+): primary items + More dropdown. No horizontal scrolling. */}
          <div className="ml-6 hidden h-16 flex-1 items-center lg:flex" data-testid="admin-desktop-nav">
            {primary.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => navigate(item.path)}
                  aria-current={active ? "page" : undefined}
                  className={navBtnClass(active)}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {item.label}
                  {active && <ActiveBar />}
                </button>
              );
            })}

            {secondary.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="admin-nav-more" className={navBtnClass(moreActive)}>
                    <MoreHorizontal className="h-4 w-4 shrink-0" />
                    More
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
                    {moreActive && <ActiveBar />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {secondary.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <DropdownMenuItem
                        key={item.path}
                        data-testid={`admin-more-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onSelect={() => navigate(item.path)}
                        className={active ? "bg-[#1C3A7A]/10 font-medium text-[#1C3A7A]" : ""}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {item.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Desktop logout — anchored right */}
          <button
            data-testid="admin-nav-logout"
            onClick={handleLogout}
            className="ml-auto hidden h-16 shrink-0 items-center gap-1.5 whitespace-nowrap px-3 text-sm font-medium text-white/70 transition-colors hover:bg-red-500/20 hover:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FAB710] focus-visible:ring-inset lg:flex"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            Logout
          </button>

          {/* Mobile / tablet menu (<lg) */}
          <div className="ml-auto lg:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="admin-mobile-menu-btn"
                  aria-label="Open admin menu"
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-white/90 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FAB710]"
                >
                  <MenuIcon className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {allItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      data-testid={`admin-mobile-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      onSelect={() => navigate(item.path)}
                      className={active ? "bg-[#1C3A7A]/10 font-medium text-[#1C3A7A]" : ""}
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="admin-mobile-logout"
                  onSelect={handleLogout}
                  className="text-red-600 focus:text-red-600"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm text-gray-500">
          <span>© 2026 RecoveryMatch</span>
          <span>•</span>
          <span>v1.0</span>
          <span>•</span>
          <a
            href="mailto:support@recoverymatch.com"
            className="text-[#1C3A7A] hover:underline"
          >
            Support
          </a>
        </div>
      </footer>
    </div>
  );
};

export default AdminLayout;
